import { Grid } from '@material-ui/core';
import { appliesAutoPxDiscount } from 'data-layer-cr/dist/functions/quotations';
import {
  CoverageCatalog,
  CoverageCatalogResume,
  Quotation,
  QuotationUnit,
} from 'data-layer-cr/dist/models';
import { useControlledExpandBehavior } from 'design-system';
import { uniqueId } from 'lodash';
import React, { useMemo } from 'react';
// constants
import { profiles } from 'src/constants/profileTypes';
// subcomponents
import { useSearchParams } from 'react-router-dom';
import { IndividualUnitResumeCard } from '../../../../UnitSelection/components/InformationCards';
import DynamicQuotationCard from '../../../../components/DynamicQuotationCard/DynamicQuotationCard';

/**
 * This container object was defined so that each quotation unit instance could be associated with a
 * unique identifier that persist between renders. This is required because units can be displayed inside
 * a list, and the "key" element is what will decide what elements need to rerender (uniqueIdentifier
 * persisting between renders will avoid each list element from being re-created).
 *
 * Ideally, the server should return an "id" reference for this object, but since it is an intermediate
 * entity there is no direct way to identify a single instance other than applying the wrapper.
 */
type UnitDisplayContainer = {
  uniqueIdentifier: string;
  unit: QuotationUnit;
};

type Props = {
  quotation: Quotation;
  coverageCatalogs: CoverageCatalog[];
  coverageCatalogResumes: CoverageCatalogResume[];
  maxAllowedPayments: number;
};

// renders different package/deducible options for the given quotation
const DeductibleOptions = ({
  quotation,
  coverageCatalogs,
  coverageCatalogResumes,
  maxAllowedPayments,
}: Props): JSX.Element => {
  const [searchParams] = useSearchParams();
  // controls expand behavior
  const { isExpanded, toggleExpandStatus } = useControlledExpandBehavior({
    defaultExpanded: '',
  });

  const isIndividualQuotation = useMemo(
    () => quotation.quotationProfile.type === profiles.INDIVIDUAL,
    [quotation],
  );

  // inject unique identifiers to each unit and sort them by price
  const mappedUnits = useMemo(() => {
    let newUnits = [...quotation.units];
    if (
      quotation.origin === 'CALCULADORA' &&
      searchParams.get('package') !== null
    ) {
      const packageId = Number(searchParams.get('package'));
      newUnits = newUnits.filter((unit) => unit.package.id === packageId);
    }
    return newUnits
      .map(
        (unit): UnitDisplayContainer => ({
          uniqueIdentifier: uniqueId(),
          unit,
        }),
      )
      .sort((a, b) => b.unit.total.grandTotal - a.unit.total.grandTotal);
  }, [quotation.units, searchParams]);

  const variantList = useMemo(
    () =>
      mappedUnits.map((mappedUnit): React.ReactNode => {
        const { uniqueIdentifier, unit } = mappedUnit;
        const onForceCollapse = (): void => {
          toggleExpandStatus(uniqueIdentifier);
        };
        return (
          <Grid item xs={12} key={uniqueIdentifier}>
            {isIndividualQuotation && (
              <IndividualUnitResumeCard
                data-testid="individual-unit-resume"
                coverageCatalogResumes={coverageCatalogResumes}
                coverageCatalogs={coverageCatalogs}
                moneyType={quotation.quotationProfile.typeOfMoney}
                packageOption={unit.package.id}
                unit={unit}
                forceCollapsed={!isExpanded(uniqueIdentifier)}
                onForceCollapsedUpdate={onForceCollapse}
                quotation={quotation}
              />
            )}
            {!isIndividualQuotation && (
              <DynamicQuotationCard
                data-testid={uniqueIdentifier}
                isAutoPx={appliesAutoPxDiscount(quotation)}
                paymentQuantity={maxAllowedPayments}
                unit={unit}
                catalogCoverages={coverageCatalogs}
                isEditable={false}
                forceCollapsed={!isExpanded(uniqueIdentifier)}
                onForceCollapsedUpdate={onForceCollapse}
                quotation={quotation}
              />
            )}
          </Grid>
        );
      }),
    [
      isExpanded,
      toggleExpandStatus,
      mappedUnits,
      coverageCatalogs,
      quotation,
      maxAllowedPayments,
      coverageCatalogResumes,
      isIndividualQuotation,
    ],
  );
  return (
    <Grid container spacing={2} data-testid="deductible-variant-list">
      {variantList}
    </Grid>
  );
};

export default DeductibleOptions;
